import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

/**
 * SCRUM-2266: the create-page wizard's validation flags, written as a
 * createSlice like every reducer in this client (SCRUM-1954) and mounted
 * LOCALLY with `useReducer(pageFormValidation.reducer, ...)` in
 * PageFormContainer — the unsaved state of the wizard, not store data. The
 * SCRUM-2127 pattern.
 *
 * The five flags used to be five `useState` calls:
 * - `formchecker`: which header fields are empty (set by the Save/Next check);
 * - `showCheckerResult`: whether the empty-field messages are shown;
 * - `isFormValid`: the template step's reported validity (gates step 1);
 * - `isValidationAttempted`: set while Next is processed, cleared on an advance;
 * - `formHasError`: the page-type step's reported error (gates step 0).
 */
type Formchecker = { globalTxt: boolean; metaDescription: boolean };

type PageFormValidationState = {
  formchecker: Formchecker;
  showCheckerResult: boolean;
  isFormValid: boolean;
  isValidationAttempted: boolean;
  formHasError: boolean;
};

const initialState: PageFormValidationState = {
  formchecker: { globalTxt: false, metaDescription: false },
  showCheckerResult: false,
  isFormValid: false,
  isValidationAttempted: false,
  formHasError: false,
};

export const pageFormValidation = createSlice({
  name: "pageFormValidation",
  initialState,
  reducers: {
    checkerSet(state, action: PayloadAction<Formchecker>) {
      state.formchecker = action.payload;
    },
    checkerResultShown(state) {
      state.showCheckerResult = true;
    },
    formValidityChanged(state, action: PayloadAction<boolean>) {
      state.isFormValid = action.payload;
    },
    errorChanged(state, action: PayloadAction<boolean>) {
      state.formHasError = action.payload;
    },
    validationAttempted(state) {
      state.isValidationAttempted = true;
    },
    stepAdvanced(state) {
      state.isValidationAttempted = false;
    },
    // After a save the wizard starts over. Only the checker and its display go
    // back: `isFormValid`, `isValidationAttempted` and `formHasError` are
    // reported by the steps and stay as they are, as they did with useState.
    wizardReset(state) {
      state.formchecker = initialState.formchecker;
      state.showCheckerResult = false;
    },
  },
});
